import type { Promisable } from "@core/foundation/promise";
import type { BrandedString } from "@core/foundation/string";
import type { ZodObject } from "@core/zod";
import { z } from "@core/zod";

import type {
  JsonPaginatedResult,
  JsonPaginationOptions,
  JsonPayload,
  JsonUpdate,
} from "./JsonRepositoryContract.ts";
import type { JsonTransactionContract } from "./JsonTransactionContract.ts";

const PK_SYMBOL = Symbol("PK");

const SK_SYMBOL = Symbol("SK");

export const SK_VERSION_STRING = { v0: "v0" } as const;

export const NoIdentifierSchema = z.object(
  { unused: z.never().optional() },
  { unused: "The placeholder of a record whose keys carry no identifier" },
);

export type PK<EntityType extends string> = BrandedString<
  typeof PK_SYMBOL,
  `${EntityType}#${string}` | EntityType
>;

export type SK = BrandedString<typeof SK_SYMBOL>;

export interface JsonRepositoryHelperContract<
  Identifier extends ZodObject,
  Data extends ZodObject,
  Transaction extends JsonTransactionContract,
  EntityType extends string,
> {
  readonly __typeTag: "JsonRepositoryHelperContract";
  /**
   * @throws {JsonDuplicateError} On duplicate.
   */
  readonly create: (
    PK: PK<EntityType>,
    SK: SK,
    payload: JsonPayload<Identifier, Data>,
  ) => Promisable<JsonPayload<Identifier, Data>>;
  readonly entityType: EntityType;
  readonly getById: (
    PK: PK<EntityType>,
    SK: SK,
  ) => Promisable<JsonPayload<Identifier, Data> | null>;
  readonly getByIds: (
    keys: readonly { readonly PK: PK<EntityType>; readonly SK: SK }[],
  ) => Promisable<readonly JsonPayload<Identifier, Data>[]>;
  readonly getByPK: (
    PK: PK<EntityType>,
    options?: JsonPaginationOptions,
  ) => Promisable<JsonPaginatedResult<Identifier, Data>>;
  readonly getBySKGt: (
    PK: PK<EntityType>,
    SK: SK,
    options?: Omit<JsonPaginationOptions, "order">,
  ) => Promisable<JsonPaginatedResult<Identifier, Data>>;
  readonly getBySKLt: (
    PK: PK<EntityType>,
    SK: SK,
    options?: Omit<JsonPaginationOptions, "order">,
  ) => Promisable<JsonPaginatedResult<Identifier, Data>>;
  readonly getBySKPrefix: (
    PK: PK<EntityType>,
    SKPrefix: SK,
    options?: JsonPaginationOptions,
  ) => Promisable<JsonPaginatedResult<Identifier, Data>>;
  readonly prepareCreate: (
    transaction: Transaction,
    PK: PK<EntityType>,
    SK: SK,
    payload: JsonPayload<Identifier, Data>,
  ) => Promisable<void>;
  readonly prepareUpdateById: (
    transaction: Transaction,
    PK: PK<EntityType>,
    SK: SK,
    update: JsonUpdate<Data>,
  ) => Promisable<void>;
  /**
   * @throws {JsonWriteConflictError} If etag provided and doesn't match.
   */
  readonly updateById: (
    PK: PK<EntityType>,
    SK: SK,
    update: JsonUpdate<Data>,
  ) => Promisable<JsonPayload<Identifier, Data> | null>;
}
